<template>
  <div v-if="visible" class="np-card np-card--success" data-test="new-ui-welcome-banner">
    <div class="np-card__icon"><i class="fa fa-star"></i></div>
    <div class="np-card__content">
      <h4 class="np-card__title">Teamio has a new look</h4>
      <p class="np-card__text">Everything you use is still here. Prefer the old layout for now? You can switch back to classic Teamio until {{ until }}.</p>
    </div>
    <div class="np-card__actions">
      <button type="button" class="np-card__cta" data-test="new-ui-welcome-dismiss" @click="dismiss">Got it</button>
      <button type="button" class="nuw__link" data-test="new-ui-welcome-switch" @click="switchToClassic">Switch to classic</button>
    </div>
  </div>
</template>

<script>
/**
 * One-time welcome banner for an OFFICE user who is on the new UI (new-UI release plan, 2026-10-10):
 * one line on what changed, "Got it", "Switch to classic". Mounted in Layout.vue's content column
 * above <FeatureNudge />, in the same np-card style as the banners beside it (styles: Layout.scss).
 *
 * Shows only when ALL hold: not a Super Admin (Teamio staff keep their own switches), an employee ID
 * exists, uiVersion.js officeSwitchState says the control is visible with target 'old' (some released
 * area draws the new UI for this person), and this employee has not dismissed it.
 *
 * Dismissal is per employee in localStorage `teamio_ui_welcome_seen:<employeeID>`. Storage can throw
 * (private window, blocked site data): a failed write only means the banner can show again on a later
 * visit — it is still hidden for the rest of this one.
 *
 * Reads the store directly and names its computeds apart from src/mixin.js's (`user`, `userID`,
 * `isSuperAdmin`) — cypress/node-tests/mixin-prop-shadow-guardrail.test.js.
 */
import { browserStorage, officeSwitchState, CLASSIC_UNTIL } from '@/utils/followups/uiVersion.js';
import { switchOfficeUi } from '@/utils/followups/uiSwitch.js';
import { formatClassicUntil } from '@/utils/followups/uiCopy.js';

/** localStorage key prefix; the employee ID follows. */
export const WELCOME_SEEN_PREFIX = 'teamio_ui_welcome_seen:';

export default {
  name: 'NewUiWelcomeBanner',

  data() {
    // Employee IDs dismissed in this page view (so a storage failure still hides the banner now).
    return { dismissedHere: [] };
  },

  computed: {
    // Demo session (customer/isDemo): no classic-Teamio prompts, the demo shows the new Teamio only (Saumil 8a).
    // Store-safe: component tests mount without a store or without getters.
    viewerIsDemo() {
      const store = this.$store;
      return !!(store && store.getters && store.getters['customer/isDemo'] === true);
    },
    customerState() {
      const store = this.$store;
      return (store && store.state && store.state.customer) || {};
    },
    viewerIsSuperAdmin() {
      const user = this.customerState.user;
      return !!(user && user.Role === 'Super Admin');
    },
    viewerKey() {
      const id = this.customerState.userID;
      return id === null || id === undefined ? '' : String(id).trim();
    },
    onNewUi() {
      if (this.viewerIsSuperAdmin || this.viewerKey === '') return false;
      const s = officeSwitchState({ isSuperAdmin: false, storage: browserStorage(), userKey: this.viewerKey, isDemo: this.viewerIsDemo === true });
      return s.visible === true && s.target === 'old';
    },
    seen() {
      if (this.dismissedHere.indexOf(this.viewerKey) !== -1) return true;
      try {
        const storage = browserStorage();
        return !!storage && storage.getItem(WELCOME_SEEN_PREFIX + this.viewerKey) === '1';
      } catch (e) {
        return false;
      }
    },
    visible() {
      return this.onNewUi && !this.seen;
    },
    until() {
      return formatClassicUntil(CLASSIC_UNTIL);
    },
  },

  methods: {
    markSeen() {
      const key = this.viewerKey;
      if (key === '') return;
      this.dismissedHere = this.dismissedHere.concat(key);
      try {
        const storage = browserStorage();
        if (storage) storage.setItem(WELCOME_SEEN_PREFIX + key, '1');
      } catch (e) { /* the banner may show again next visit */ }
    },
    dismiss() {
      this.markSeen();
    },
    // Asks "the page will reload" first. The banner is marked seen ONLY once the person confirms (and
    // the choice is stored); on cancel it stays exactly as it was.
    switchToClassic() {
      switchOfficeUi('old', { userKey: this.viewerKey, from: 'welcome-banner', onConfirmed: this.markSeen });
    },
  },
};
</script>

<style lang="scss" scoped>
// "Switch to classic": a quiet text button next to the primary "Got it" (the banner's green).
.nuw__link {
  background: none;
  border: none;
  padding: 4px 6px;
  font-size: 12px;
  font-weight: 500;
  color: #2d9474;
  text-decoration: underline;
  white-space: nowrap;
  cursor: pointer;

  &:hover { color: #1f6b53; }
}
</style>
